// Restore keys (PMU-245; Google Play's sign-in restoration rule from April 2027): once signed in,
// the Android apps save a restore key with Credential Manager. It travels with the phone's backup,
// and on a new phone the sign-in screen reads it back and signs in with no tap. The server accepts
// it for sign-in only, never as a second factor (worker/api/passkey-routes.ts).
import { useEffect, useRef } from "react";
import { api } from "./api.ts";
import type { AppPlatform } from "./platform.ts";

export interface RestoreKeyBridge {
  create(requestJson: string): Promise<string>;
  /** null when this phone has no restore key. */
  get(requestJson: string): Promise<string | null>;
  clear(): Promise<void>;
}

const SAVED = "pmu.restore_key";
const saved = () => { try { return localStorage.getItem(SAVED) === "1"; } catch { return false; } };
const mark = (v: boolean) => { try { if (v) localStorage.setItem(SAVED, "1"); else localStorage.removeItem(SAVED); } catch { /* saved again next time */ } };

async function save(bridge: RestoreKeyBridge) {
  const o = await api<{ options: unknown; challenge_id: string }>("POST", "/api/v1/auth/passkey/register/options", { restore: true });
  const response = JSON.parse(await bridge.create(JSON.stringify(o.options)));
  await api("POST", "/api/v1/auth/passkey/register/verify", { challenge_id: o.challenge_id, response, name: "Restore key" });
  mark(true);
}

async function restore(bridge: RestoreKeyBridge, client: "guardian_app" | "teacher_app") {
  const o = await api<{ options: unknown; challenge_id: string }>("POST", "/api/v1/auth/passkey/login/options", { restore: true });
  const json = await bridge.get(JSON.stringify(o.options));
  if (!json) return null;
  return api<{ token: string; expires_at: number }>("POST", "/api/v1/auth/passkey/login/verify", { challenge_id: o.challenge_id, response: JSON.parse(json), client });
}

/** Signed in: saves this install's restore key once. Signed out: tries it once, silently. */
export function useRestoreKey(platform: AppPlatform, session: "loading" | "in" | "out", client: "guardian_app" | "teacher_app" | "web", onSession: (r: { token: string; expires_at: number }) => void) {
  const tried = useRef(false);
  const bridge = platform.restoreKey;
  useEffect(() => {
    if (!bridge || client === "web") return;
    if (session === "in" && !saved()) void save(bridge).catch(() => undefined);
    if (session === "out" && !tried.current) {
      tried.current = true;
      void restore(bridge, client).then((r) => { if (r?.token) onSession(r); }, () => undefined);
    }
  }, [bridge, session, client]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** On sign-out: the key goes, so a restored phone does not sign this person back in. */
export async function clearRestoreKey(platform: AppPlatform) {
  mark(false);
  await platform.restoreKey?.clear().catch(() => undefined);
}
